/**
 * Authentication Provider
 *
 * React Context provider for server authentication
 * Handles automatic token exchange, refresh, and state management
 */

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useContext,
  type ReactNode,
} from "react";
import { AuthContext, type AuthContextValue } from "./AuthContext";
import { AuthService } from "@/services/auth-service";
import { LiffContext } from "./infrastructure/LineContext";
import {
  loadAuthSession,
  saveAuthSession,
  clearAuthSession,
  getAccessToken,
} from "./auth-storage";
import { apiClient } from "@/lib/api-client";
import type {
  UserInfo,
  AvailableRole,
  AuthState,
  AuthSession,
} from "@/types/auth";

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Retry with exponential backoff
 * @param fn - Function to retry
 * @param maxAttempts - Maximum retry attempts (default: 3)
 * @returns Result of function
 */
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxAttempts: number = 3,
): Promise<T> {
  let lastError: Error;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      // Don't retry on 401 (user not registered) or 400 (bad request)
      console.log("error", error);
      const statusCode = (error as { statusCode?: number }).statusCode;
      console.log("statusCode", statusCode);
      if (statusCode === 401 || statusCode === 400) {
        throw error;
      }

      // Exponential backoff: 2^attempt seconds (1s, 2s, 4s)
      if (attempt < maxAttempts - 1) {
        const delay = Math.pow(2, attempt) * 1000;
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError!;
}

/**
 * AuthProvider
 *
 * Server authentication provider that manages token lifecycle, automatic refresh, and cross-tab synchronization.
 * Uses React 19 direct context rendering pattern for improved performance.
 *
 * **React 19 Context Pattern:**
 * - Returns `<AuthContext value={...}>` instead of `<AuthContext.Provider value={...}>`
 * - Context value is memoized with useMemo to prevent unnecessary consumer re-renders
 * - Only state variables in dependency array (methods are stable, excluded per React 19 best practices)
 *
 * **Token Lifecycle Management:**
 * 1. **Automatic Exchange**: When LINE login detected, exchanges LINE tokens for server tokens
 * 2. **Proactive Refresh**: Refreshes access token 5 minutes before expiry
 * 3. **Reactive Refresh**: On 401 response, attempts token refresh via TokenRefreshQueue
 * 4. **CSRF Rotation**: Updates localStorage with rotated CSRF token after each refresh
 * 5. **Cross-Tab Sync**: Listens to localStorage changes to sync auth state across tabs
 *
 * **State Machine:**
 * - `LOGGED_OUT` → User has no valid session
 * - `AUTHENTICATING` → Token exchange in progress
 * - `AUTHENTICATED` → Valid server session with access token
 * - `ERROR` → Token exchange/refresh failed (e.g., user not registered)
 *
 * **Authentication Flow:**
 * ```
 * LINE Login → Exchange LINE tokens for server tokens → Save to localStorage
 *           ↓
 * Set up refresh queue → Proactive refresh (5 min before expiry)
 *                      ↓
 * API call with access token → 401? → Reactive refresh → Update token
 * ```
 *
 * **Memoization Strategy:**
 * - `loadExistingSession`: useCallback (used in useEffect deps)
 * - `exchangeTokens`: useCallback (used in useEffect deps)
 * - `logout`: NOT memoized (infrequent call, not in deps)
 * - `switchRole`: NOT memoized (infrequent call, not in deps)
 * - Context value: useMemo (state vars only, methods excluded)
 *
 * **Edge Case Handling:**
 * - Prevents infinite retry loops on 401 with `hasAttemptedExchange` ref
 * - Deduplicates concurrent refresh requests via TokenRefreshQueue
 * - Handles localStorage disabled with error state
 * - Syncs logout/refresh across tabs via StorageEvent listener
 *
 * @param children - Child components that will have access to auth context
 *
 * @example
 * ```tsx
 * <AuthProvider>
 *   <ProtectedApp />
 * </AuthProvider>
 * ```
 */
export function AuthProvider({ children }: AuthProviderProps) {
  // LINE authentication context (internal infrastructure)
  // Directly consume LiffContext instead of using useLiff hook (no longer public API)
  const liffContext = useContext(LiffContext);
  if (!liffContext) {
    throw new Error("AuthProvider must be used within LiffProvider");
  }

  const {
    idToken: lineIdToken,
    accessToken: lineAccessToken,
    isLoggedIn: isLineLoggedIn,
    isLiffReady,
  } = liffContext;

  // Authentication state
  const [authState, setAuthState] = useState<AuthState>("LOGGED_OUT");
  const [user, setUser] = useState<UserInfo | null>(null);
  const [availableRoles, setAvailableRoles] = useState<AvailableRole[]>([]);
  const [currentRole, setCurrentRole] = useState<AvailableRole | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [tokenExpiry, setTokenExpiry] = useState<number | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // Track if we've already attempted token exchange (to prevent retry loops)
  const hasAttemptedExchange = useRef(false);

  // Derived state
  const isLoggedIn = authState === "AUTHENTICATED";
  const isAuthenticating = authState === "AUTHENTICATING";
  const userId = user?.id ?? null;

  /**
   * Load existing session from localStorage
   *
   * **Why useCallback?**
   * This function is included in useEffect dependency arrays (line 242), so it MUST be
   * memoized to prevent infinite re-render loops. Without useCallback, the function
   * would be recreated on every render, triggering the useEffect, which calls this
   * function, which triggers a state update, which triggers a re-render... infinite loop.
   *
   * **What it does:**
   * 1. Reads auth session from localStorage (access_token, user, etc.)
   * 2. If valid session exists → Set AUTHENTICATED state with user data
   * 3. If no session → Set LOGGED_OUT state and clear all auth state
   *
   * **When called:**
   * - On mount (initial load)
   * - On storage event (cross-tab sync)
   *
   * @returns void - Updates component state with session data
   */
  const loadExistingSession = useCallback(() => {
    const session = loadAuthSession();
    if (session) {
      setAuthState("AUTHENTICATED");
      setUser(session.user);
      setAccessToken(session.accessToken);
      setTokenExpiry(session.tokenExpiry);
      // Find current role from available roles (will be set from token exchange)
      setError(null);
    } else {
      // Clear state if session is invalid
      setAuthState("LOGGED_OUT");
      setUser(null);
      setAvailableRoles([]);
      setCurrentRole(null);
      setAccessToken(null);
      setTokenExpiry(null);
    }
  }, []);

  /**
   * Exchange LINE tokens for server tokens
   *
   * **Why useCallback?**
   * This function is included in useEffect dependency arrays (line 304), so it MUST be
   * memoized to prevent infinite re-render loops. Without useCallback, the function
   * would be recreated on every render, triggering the useEffect, which might call
   * this function, which triggers state updates, leading to potential infinite loops.
   *
   * **Token Exchange Flow:**
   * 1. Set state to AUTHENTICATING
   * 2. Call backend `/auth/exchange` with LINE ID token and LINE access token
   * 3. Backend validates LINE tokens with LINE API
   * 4. Backend returns: server access_token, csrf_token, id_token, user profile, available_roles
   * 5. Save session to localStorage (all tokens + user data + role)
   * 6. Update component state to AUTHENTICATED
   *
   * **Retry Logic:**
   * Uses exponential backoff (1s, 2s, 4s) for network errors.
   * Does NOT retry on 401 (user not registered) or 400 (bad request).
   *
   * **Error Handling:**
   * - On 401: User not registered → Set ERROR state, clear session
   * - On network error: Retry with backoff up to 3 attempts
   * - On other errors: Set ERROR state, clear session
   *
   * **hasAttemptedExchange Ref:**
   * Prevents infinite retry loops. Once we attempt exchange and get 401 (user not registered),
   * we set this ref to true and never attempt exchange again until page reload.
   *
   * @param lineIdToken - LINE ID token (JWT from LIFF SDK)
   * @param lineAccessToken - LINE access token (for LINE API calls)
   * @returns Promise<void> - Updates component state on success/failure
   */
  const exchangeTokens = useCallback(
    async (lineIdToken: string, lineAccessToken: string) => {
      try {
        setAuthState("AUTHENTICATING");
        setError(null);

        // Exchange tokens with retry logic
        const response = await retryWithBackoff(() =>
          AuthService.exchangeLineToken(lineIdToken, lineAccessToken),
        );

        // Calculate token expiry timestamp
        const tokenExpiry = Date.now() + response.expires_in * 1000;

        // Find customer role as default
        const customerRole =
          response.available_roles.find(
            (role) => role.role_type === "customer",
          ) || response.available_roles[0];

        // Save session to localStorage
        const session: AuthSession = {
          accessToken: response.access_token,
          csrfToken: response.csrf_token,
          idToken: response.id_token,
          tokenExpiry,
          user: response.user,
          roleId: customerRole.role_id,
          clientId: "liff-app",
        };

        saveAuthSession(session);

        // Update state
        setAuthState("AUTHENTICATED");
        setUser(response.user);
        setAvailableRoles(response.available_roles);
        setCurrentRole(customerRole);
        setAccessToken(response.access_token);
        setTokenExpiry(tokenExpiry);
        setError(null);
      } catch (err) {
        const error = err as Error;
        console.error("Token exchange failed:", error);

        setAuthState("ERROR");
        setError(error);

        // Clear any partial state
        clearAuthSession();
        setUser(null);
        setAvailableRoles([]);
        setCurrentRole(null);
        setAccessToken(null);
        setTokenExpiry(null);
      }
    },
    [],
  );

  /**
   * Load session from localStorage on mount
   */
  useEffect(() => {
    loadExistingSession();
  }, [loadExistingSession]);

  /**
   * Cross-tab synchronization via StorageEvent
   * Listen for auth_access_token changes from other tabs
   */
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // Only respond to auth_access_token changes from other tabs
      if (event.key !== "auth_access_token") return;
      // Check storageArea if present (production), allow if absent (test environment)
      if (event.storageArea && event.storageArea !== localStorage) return;

      // Token was removed (logout in another tab)
      if (event.newValue === null) {
        // Clear state
        setAuthState("LOGGED_OUT");
        setUser(null);
        setAvailableRoles([]);
        setCurrentRole(null);
        setAccessToken(null);
        setTokenExpiry(null);
        setError(null);
        return;
      }

      // Token was added/updated (login/refresh in another tab)
      // Reload session from localStorage
      loadExistingSession();
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [loadExistingSession]);

  /**
   * Automatically exchange tokens when LINE login detected
   */
  useEffect(() => {
    if (!isLiffReady) return;
    if (!isLineLoggedIn || !lineIdToken || !lineAccessToken) return;
    // Don't retry if already authenticated or authenticating
    if (authState === "AUTHENTICATED" || authState === "AUTHENTICATING") return;
    // Don't retry if we've already attempted exchange (prevents infinite loops on 401)
    if (hasAttemptedExchange.current) return;

    // Mark that we're attempting exchange
    hasAttemptedExchange.current = true;

    // Trigger token exchange with both LINE ID token and LINE access token
    exchangeTokens(lineIdToken, lineAccessToken);
  }, [
    isLiffReady,
    isLineLoggedIn,
    lineIdToken,
    lineAccessToken,
    authState,
    exchangeTokens,
  ]);

  /**
   * Set access token getter for apiClient
   * This ensures all API requests automatically include the current valid access token
   */
  useEffect(() => {
    // Set the access token getter function
    // It will be called for each API request to get a fresh token
    apiClient.setAccessTokenGetter(getAccessToken);

    // Clean up on unmount
    return () => {
      apiClient.setAccessTokenGetter(null);
    };
  }, []); // Empty deps - set once on mount

  /**
   * Set up token refresh queue
   * This handles automatic token refresh when tokens expire
   */
  useEffect(() => {
    const refreshFunction = async () => {
      try {
        // Get current CSRF token and role ID from storage
        const csrfToken = localStorage.getItem("auth_csrf_token");
        const roleId = localStorage.getItem("auth_role_id");

        if (!csrfToken || !roleId) {
          throw new Error("Missing CSRF token or role ID");
        }

        // Call refresh endpoint
        const response = await AuthService.refreshToken(csrfToken, roleId);

        // Update tokens in storage
        const tokenExpiry = Date.now() + response.expires_in * 1000;
        localStorage.setItem("auth_access_token", response.access_token);
        localStorage.setItem("auth_id_token", response.id_token);
        localStorage.setItem("auth_token_expiry", tokenExpiry.toString());

        // Update CSRF token if backend rotated it
        // Backend may rotate CSRF tokens on each refresh for additional security
        if (response.csrf_token) {
          localStorage.setItem("auth_csrf_token", response.csrf_token);
        }

        // Update state
        setAccessToken(response.access_token);
        setTokenExpiry(tokenExpiry);
      } catch (error) {
        // Refresh failed - likely refresh token expired
        console.error("Token refresh failed:", error);

        // Check if it's a 401 (refresh token expired)
        const statusCode = (error as { statusCode?: number }).statusCode;
        if (statusCode === 401) {
          // Refresh token expired - clear session and redirect to login
          clearAuthSession();
          setAuthState("LOGGED_OUT");
          setUser(null);
          setAvailableRoles([]);
          setCurrentRole(null);
          setAccessToken(null);
          setTokenExpiry(null);
          setError(new Error("Session expired. Please log in again."));
        }

        // Always propagate error so retry logic works correctly
        throw error;
      }
    };

    // Set up the refresh queue
    apiClient.setRefreshQueue(refreshFunction);

    // No cleanup needed - queue persists for app lifetime
  }, []); // Empty deps - set once on mount

  /**
   * Logout and clear session
   */
  const logout = async (): Promise<void> => {
    try {
      // Revoke refresh token on backend (best effort)
      await AuthService.revokeToken();
    } catch (error) {
      // Ignore revoke errors - we'll clear local state regardless
      console.warn("Token revoke failed during logout:", error);
    }

    // Clear local session
    clearAuthSession();

    // Reset state
    setAuthState("LOGGED_OUT");
    setUser(null);
    setAvailableRoles([]);
    setCurrentRole(null);
    setAccessToken(null);
    setTokenExpiry(null);
    setError(null);
  };

  /**
   * Switch to different role
   */
  const switchRole = async (roleId: string): Promise<void> => {
    try {
      // Find the requested role
      const requestedRole = availableRoles.find(
        (role) => role.role_id === roleId,
      );
      if (!requestedRole) {
        throw new Error(`Role ${roleId} not found in available roles`);
      }

      // Get CSRF token from storage
      const csrfToken = localStorage.getItem("auth_csrf_token");
      if (!csrfToken) {
        throw new Error("Missing CSRF token");
      }

      // Refresh token with new role
      const response = await AuthService.refreshToken(csrfToken, roleId);

      // Calculate token expiry timestamp
      const tokenExpiry = Date.now() + response.expires_in * 1000;

      // Update tokens in storage
      localStorage.setItem("auth_access_token", response.access_token);
      localStorage.setItem("auth_id_token", response.id_token);
      localStorage.setItem("auth_token_expiry", tokenExpiry.toString());
      localStorage.setItem("auth_role_id", roleId);

      // Update state
      setCurrentRole(requestedRole);
      setAccessToken(response.access_token);
      setTokenExpiry(tokenExpiry);
      setError(null);
    } catch (error) {
      const err = error as Error;
      console.error("Role switch failed:", err);
      setError(err);
      throw err;
    }
  };

  /**
   * Memoize context value to prevent unnecessary re-renders of consumers
   *
   * **Why useMemo?**
   * Without memoization, the context value object would be recreated on every render,
   * causing ALL consumers (any component using useAuth()) to re-render even when
   * state hasn't changed. This is a critical performance optimization for auth context
   * which is consumed by many components throughout the app.
   *
   * **Dependency Array Strategy (React 19 Best Practice):**
   * - **Include**: State variables (isLoggedIn, user, accessToken, etc.)
   * - **Exclude**: Methods (logout, switchRole) - these are stable function references
   *
   * **Why exclude methods from dependencies?**
   * 1. `logout` and `switchRole` don't close over any changing values (they access state directly)
   * 2. React 19 Compiler handles stable function optimization automatically
   * 3. Including them would cause unnecessary re-memoization on every render
   * 4. They're defined at module scope, not recreated on each render
   *
   * **ESLint Disable Justification:**
   * ESLint's exhaustive-deps rule wants us to include logout and switchRole, but this
   * would be counterproductive. We suppress the warning because we understand the
   * memoization strategy and have verified these functions are stable.
   *
   * This pattern ensures consumers ONLY re-render when actual auth state changes
   * (login, logout, token refresh, role switch), not on every parent re-render.
   */
  const contextValue = useMemo<AuthContextValue>(
    () => ({
      isLoggedIn,
      isAuthenticating,
      authState,
      user,
      userId,
      availableRoles,
      currentRole,
      accessToken,
      tokenExpiry,
      error,
      logout,
      switchRole,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isLoggedIn,
      isAuthenticating,
      authState,
      user,
      userId,
      availableRoles,
      currentRole,
      accessToken,
      tokenExpiry,
      error,
    ],
  );

  // React 19: Use direct context rendering instead of Context.Provider
  return <AuthContext value={contextValue}>{children}</AuthContext>;
}
